import { Alert, Badge, Button, Form, InputGroup } from 'react-bootstrap';
import { ROLLOUT_STATE } from '@prerender/feature-flags';

import { RequireTier } from '../auth/RequireTier';
import { FeatureFlag, FeatureFlagState, RELEASE_FLAG_STATES } from './FeatureFlag';

const STATE_DROPDOWN = { width: '8rem' };
const PERCENT_INPUT = { width: '8rem' };

type FeatureFlagRowProps = {
  flag: FeatureFlag;
  shownState: FeatureFlagState;
  unsaved: boolean;
  percentText: string;
  percentInvalid: boolean;
  saveError: string | undefined;
  onPick: (state: FeatureFlagState) => void;
  onPercentChange: (text: string) => void;
  onGrant: () => void;
};

function FeatureFlagRow({
  flag,
  shownState,
  unsaved,
  percentText,
  percentInvalid,
  saveError,
  onPick,
  onPercentChange,
  onGrant,
}: FeatureFlagRowProps) {
  const dropdownId = `feature-flag-${flag.name}`;
  const percentFeedbackId = `feature-flag-${flag.name}-percent-feedback`;

  return (
    <tr>
      <td>
        <Form.Label htmlFor={dropdownId} className="mb-0 fs-5">
          {flag.name}
        </Form.Label>
      </td>
      <td className="text-end">
        {flag.kind === 'entitlement' && <span className="text-muted small">entitlement — set per account</span>}
        {saveError && (
          <Alert variant="danger" className="d-inline-block me-2 mb-0 py-1">
            {saveError}
          </Alert>
        )}
        {unsaved && (
          <Badge bg="warning" text="dark">
            unsaved
          </Badge>
        )}
      </td>
      <td className="pe-0" style={STATE_DROPDOWN}>
        {flag.kind === 'entitlement' ? (
          <Form.Select id={dropdownId} style={STATE_DROPDOWN} value="" disabled>
            <option value="">—</option>
          </Form.Select>
        ) : (
          <>
            <RequireTier requiredTier="superadmin" policy="disable">
              <Form.Select
                id={dropdownId}
                style={STATE_DROPDOWN}
                value={shownState}
                onChange={(event) => onPick(event.target.value as FeatureFlagState)}
              >
                {RELEASE_FLAG_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </Form.Select>
            </RequireTier>
            {shownState === ROLLOUT_STATE && (
              <>
                <InputGroup className="mt-1" style={PERCENT_INPUT}>
                  <RequireTier requiredTier="superadmin" policy="disable">
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      aria-label={`Percent for ${flag.name}`}
                      aria-describedby={percentInvalid ? percentFeedbackId : undefined}
                      className={percentInvalid ? 'border-danger' : undefined}
                      value={percentText}
                      onChange={(event) => onPercentChange(event.target.value)}
                    />
                  </RequireTier>
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup>
                {percentInvalid && (
                  <Form.Control.Feedback id={percentFeedbackId} type="invalid" className="d-block">
                    Use 0–100
                  </Form.Control.Feedback>
                )}
              </>
            )}
          </>
        )}
      </td>
      <td className="text-end">
        <RequireTier requiredTier="superadmin" policy="disable">
          <Button size="sm" variant="outline-primary" onClick={onGrant}>
            Grant to accounts
          </Button>
        </RequireTier>
      </td>
    </tr>
  );
}

export default FeatureFlagRow;
